// Recent grand-prize winners showcase. Names only (no phone) — social proof
// that the ₹5,000 free service is real and being given away.
import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { api, fmtDate } from "@/lib/api";
import { trackWinnersWallView } from "@/lib/pixel";

function initials(name) {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("");
}

function maskName(full) {
  // "Ravi Kumar" -> "Ravi K." ; "Priya" -> "Priya"
  const parts = (full || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "Anonymous";
  if (parts.length === 1) return parts[0];
  return `${parts[0]} ${parts[parts.length - 1][0].toUpperCase()}.`;
}

export default function RecentWinners() {
  const [winners, setWinners] = useState([]);
  const [loading, setLoading] = useState(true);
  const sectionRef = useRef(null);
  const viewedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    api.get("/winners?limit=12")
      .then(({ data }) => { if (!cancelled) setWinners(data.winners || []); })
      .catch(() => { /* silent */ })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el || viewedRef.current) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !viewedRef.current) {
            viewedRef.current = true;
            trackWinnersWallView();
            obs.disconnect();
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [loading]);

  if (loading) return null;

  const hasWinners = winners.length > 0;

  return (
    <section
      id="winners"
      ref={sectionRef}
      data-testid="recent-winners"
      className="bg-white px-5 lg:px-8 py-14 sm:py-20 border-b border-[#E4E4E7]"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="flex items-end justify-between flex-wrap gap-3"
        >
          <div>
            <p className="text-xs uppercase tracking-[0.25em] text-[#D4AF37] font-bold mb-3">
              🏆 Free Service Winners
            </p>
            <h2 className="text-3xl sm:text-4xl font-semibold tracking-tight text-[#0A0A0A]">
              Real Customers. <span className="text-[#D4AF37]">Real Free Services.</span>
            </h2>
            <p className="mt-3 text-[#52525B] max-w-xl">
              Customers who won a complimentary car service worth ₹5,000 at AutoHQ Ranigunj. You could be next.
            </p>
          </div>
        </motion.div>

        {hasWinners ? (
          <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {winners.map((w, i) => (
              <motion.div
                key={`${w.week_key}-${i}`}
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: Math.min(i * 0.04, 0.4) }}
                className="group flex items-center gap-4 bg-[#FAFAFA] border border-[#E4E4E7] rounded-sm px-4 py-3.5 hover:border-[#D4AF37] transition-colors"
                data-testid={`winner-card-${i}`}
              >
                <div className="shrink-0 w-11 h-11 rounded-full bg-[#0A0A0A] text-[#D4AF37] flex items-center justify-center font-bold text-sm tracking-wider">
                  {initials(w.name) || "★"}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-bold text-[#0A0A0A] truncate">
                    {maskName(w.name)}
                  </p>
                  <p className="text-[11px] text-[#71717A] truncate">
                    {w.car ? `${w.car} · ` : ""}Won {w.won_at ? fmtDate(w.won_at) : "recently"}
                  </p>
                </div>
                <span className="shrink-0 text-[10px] uppercase tracking-[0.18em] font-bold text-[#D4AF37] border border-[#D4AF37]/40 rounded-sm px-2 py-1">
                  ₹5K
                </span>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-10 rounded-sm border border-dashed border-[#D4AF37]/60 bg-[#FFFCEF] px-6 py-10 text-center"
            data-testid="winners-empty-state"
          >
            <p className="text-xs uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
              No winners yet
            </p>
            <p className="mt-3 text-xl sm:text-2xl font-semibold text-[#0A0A0A]">
              Be the first to win a FREE service worth ₹5,000.
            </p>
            <p className="mt-2 text-sm text-[#52525B]">
              Spin once. The next name on this wall could be yours.
            </p>
          </motion.div>
        )}
      </div>
    </section>
  );
}
